"use client";

// imports
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

// lib files
import { clientMetricLogger } from '@/lib/logger-client';
import { LOCALIZATION_VERIFICATION_METRICS } from '@/lib/metric-strings';
import {
  locateAtom,
  PageStatusEnum,
} from '@/lib/atoms/location-verification';
import { PRODUCTION_APP_ENV_STRING } from "@/lib/constants";
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { useHasMounted } from '@/lib/hooks';

// components
import GetLocateCode from '@/components/locate/GetLocateCode/GetLocateCode';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import LocateAuthorized from '@/components/locate/LocateAuthorized/LocateAuthorized';
import LocateCodeInput from '@/components/locate/LocateCodeInput/LocateCodeInput';
import LocateUnauthorized from '@/components/locate/LocateUnauthorized/LocateUnauthorized';
import ShareLocationPrompt from '@/components/locate/ShareLocationPrompt/ShareLocationPrompt';

// styles
import styles from './LocateFlow.module.scss';

// For Analytics
const FEATURE_CATEGORY = 'localization verification';

/**
 * LocateFlow
 * The orchestration component for the location verification flow.
 * @returns {JSX.Element}
*/
export default function LocateFlow(props: { appEnv?: string }) {
  const { appEnv } = props;
  const hasMounted = useHasMounted();
  const [locateState, setLocateState] = useAtom(locateAtom);

  const validateLocateCode = useCallback(async (locateCode: string) => {
    try {
      const validateResp = await fetch(`/api/v1/locate/validatelocatecode/?locateCode=${locateCode}`);
      const respJson = await validateResp.json();
      switch(true) {
        case respJson.status === 'waiting':
          setLocateState({
            ...locateState,
            locateCodeError: null,
            pageStatus: PageStatusEnum.shareLocation
          })
          break;
        case respJson.status === 'authorized':
          sendPbsGtmEvent({
            feature_category: FEATURE_CATEGORY,
            field_entry_value: locateState.locateCode,
            feature_name: "localization verification: code check",
            object_action: "error: token already used",
          });
          clientMetricLogger(
            LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
            LOCALIZATION_VERIFICATION_METRICS.FAILURE_TOKEN_USED,
          );
          setLocateState({
            ...locateState,
            locateCodeError: "This code has already been used. Please select “Get New Code” on your device to receive a new code.",
            pageStatus: PageStatusEnum.locateCodeInput
          })
          break;
        case respJson.status === 'error':
        default:
          sendPbsGtmEvent({
            feature_category: FEATURE_CATEGORY,
            field_entry_value: locateState.locateCode,
            feature_name: "localization verification: code check",
            object_action: "error from API",
          });
          clientMetricLogger(
            LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
            LOCALIZATION_VERIFICATION_METRICS.FAILURE_ERROR_FROM_API,
          );
          setLocateState({
            ...locateState,
            locateCodeError: "We’re having trouble with this code. Please double check that you’ve entered the code correctly, or select “Get New Code” on your device.",
            pageStatus: PageStatusEnum.locateCodeInput,
          });
      }
    } catch (_error) {
      sendPbsGtmEvent({
        feature_category: FEATURE_CATEGORY,
        field_entry_value: locateState.locateCode,
        feature_name: "localization verification: code check",
        object_action: "error",
      });
      clientMetricLogger(
        LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
        LOCALIZATION_VERIFICATION_METRICS.FAILURE_ERROR,
      );
      setLocateState({
        ...locateState,
        locateCodeError: "We’re having trouble on our end. Please try again later.",
        pageStatus: PageStatusEnum.locateCodeInput,
      });
    }
  }, [locateState, setLocateState]);


  const verifyLocateCodeLatLong = useCallback(async (locateCode: string, lat: number, long: number) => {
    try {
      const verifyResp = await fetch(`/api/v1/locate/verifylocatecodelatlong/?locateCode=${locateCode}&lat=${lat}&long=${long}`);
      const respJson = await verifyResp.json();
      switch(true) {
        case respJson.status === 'authorized':
          sendPbsGtmEvent({
            feature_category: FEATURE_CATEGORY,
            object_action: "user is authorized",
          });
          clientMetricLogger(
            LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
            LOCALIZATION_VERIFICATION_METRICS.SUCCESS_AUTHORIZED,
          );
          setLocateState({
            ...locateState,
            verifiedStationShortCommonName: respJson.station_short_common_name,
            pageStatus: PageStatusEnum.locateAuthorized,
          })
          break;
        case respJson.status === 'unauthorized':
        default:
          sendPbsGtmEvent({
            feature_category: FEATURE_CATEGORY,
            object_action: "user is unauthorized",
          });
          clientMetricLogger(
            LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
            LOCALIZATION_VERIFICATION_METRICS.SUCCESS_UNAUTHORIZED,
          );
          setLocateState({
            ...locateState,
            verifiedStationShortCommonName: respJson.station_short_common_name,
            pageStatus: PageStatusEnum.locateUnauthorized,
          });
      }
    } catch (_error) {
      sendPbsGtmEvent({
        feature_category: FEATURE_CATEGORY,
        field_entry_value: locateState.locateCode,
        feature_name: "localization verification: code and location check",
        object_action: "error",
      });
      clientMetricLogger(
        LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
        LOCALIZATION_VERIFICATION_METRICS.FAILURE_ERROR,
      );
      setLocateState({
        ...locateState,
        locateCodeError: "We’re having trouble on our end. Please try again later.",
        pageStatus: PageStatusEnum.locateCodeInput,
      });
    }
  }, [locateState, setLocateState]);

  useEffect(() => {
    switch(locateState.pageStatus) {
      case PageStatusEnum.locateCodeSubmitLoading:
        if (!locateState.locateCode) {
          // this shouldn't be possible to get to, but just in case
          setLocateState({
            ...locateState,
            locateCodeError: "Please enter a code.",
            pageStatus: PageStatusEnum.locateCodeInput,
          });
          break;
        }
        sendPbsGtmEvent({
          feature_category: FEATURE_CATEGORY,
          feature_name: "localization verification: code check",
          object_action: "code submitted",
        });
        validateLocateCode(locateState.locateCode);
        break;
      case PageStatusEnum.locateCodeAndLocationLoading:
        const { locateCode, lat, long } = locateState;
        if (!locateCode || !lat || !long) {
          // this shouldn't be possible to get to, but just in case
          setLocateState({
            ...locateState,
            locateCodeError: "Please enter a code and share your location.",
            pageStatus: PageStatusEnum.locateCodeInput,
          });
          break;
        }
        sendPbsGtmEvent({
          feature_category: FEATURE_CATEGORY,
          feature_name: "localization verification: code and location check",
          object_action: "code, lat, long submitted",
        });
        clientMetricLogger(
          LOCALIZATION_VERIFICATION_METRICS.NAMESPACE,
          LOCALIZATION_VERIFICATION_METRICS.ATTEMPT,
        );
        verifyLocateCodeLatLong(locateCode, lat, long);
        break;
      default:
        break;
    };

    return () => {}
  }, [locateState, setLocateState, validateLocateCode, verifyLocateCodeLatLong]);

  const isLoading = (
    (locateState.pageStatus === PageStatusEnum.locateCodeSubmitLoading) ||
    (locateState.pageStatus === PageStatusEnum.locateCodeAndLocationLoading)
  );

  // Checks if component is mounted on the client before attempting to
  // hydrate, otherwise a hydration error occurs
  if (!hasMounted) {
    return null;
  }

  return (
    <div className={styles.locate_content}>
      { locateState.pageStatus === PageStatusEnum.locateCodeInput && (
        <LocateCodeInput />
      )}

      { isLoading && (
        <LoadingSpinner
          className={styles.locate_loading}
        />
      )}

      { locateState.pageStatus === PageStatusEnum.shareLocation && (
        <ShareLocationPrompt />
      )}

      { locateState.pageStatus === PageStatusEnum.locateUnauthorized && (
        <LocateUnauthorized />
      )}

      { locateState.pageStatus === PageStatusEnum.locateAuthorized && (
        <LocateAuthorized />
      )}

      {/* Only include GetLocateCode in dev (where APP_ENV isn't set), or non-prod environments */}
      {(!appEnv || (appEnv && appEnv !== PRODUCTION_APP_ENV_STRING)) && (
        <GetLocateCode />
      )}

      { process.env.NODE_ENV === 'development' && (
        <>
          <pre>
              {JSON.stringify(locateState, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
};
